import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import axios from '../../utils/axiosInterceptor';
import { DateTime } from 'luxon';

import {
    Paper, Fab, Typography, Tooltip, Button, Container,
    Dialog, DialogActions, DialogContent, DialogTitle,
    FormControl, TextField
} from '@mui/material';

import {
    DataGrid,
    gridPageCountSelector,
    GridPagination,
    useGridApiContext,
    GridToolbar,
    useGridSelector,
} from '@mui/x-data-grid';
import MuiPagination from '@mui/material/Pagination';

// Custom components and functions
import CustomChip from '../../utils/components/CustomChip';
import CustomMessage from '../../utils/components/CustomMessage';
import CustomStatusChip from '../../utils/components/CustomStatusChip';
import useWindowDimensions from '../../utils/functions/useWindowDimensions';
import { formatMoney, getDate } from '../../utils/functions/Utils';
import LocaleTextDataGrid from '../../utils/functions/LocaleTextDataGrid';

const TablaCotizaciones = () => {

    let navigate = useNavigate();
    const auth = useSelector((state) => state.auth);

    const [data, setData] = useState([]);
    const [selected, setSelected] = useState('');

    const [open, setOpen] = useState(false);
    const [key, setKey] = useState('');

    const [apiRes, setApiRes] = useState({ show: false, message: '', severity: '' });
    const { height } = useWindowDimensions();

    useEffect(() => {
        if (!['Admin', 'Vendedor', 'Ayudante'].includes(auth.user.rol)) {
            navigate('/dashboard');
        }
        document.title = 'Tabla de pedidos';
    }, [auth])

    const handleCloseMessage = (show) => {
        setApiRes(state => { return { ...state, show: show } })
    }

    const getData = async () => {
        try {
            let res;
            if (auth.user.rol === 'Admin')
                res = await axios.get('/api/orders');
            else
                res = await axios.get(`/api/orders/seller/${auth.user.name}`);
            if (res.status === 200) {
                // setData(res.data);
                if (res.data.length <= 1) {
                    res.data[0].fechaDeCotizacion = DateTime.fromISO(res.data[0].fechaDeCotizacion).toJSDate();
                    res.data[0].fechaDeEntrega = getDate(res.data[0].fechaDeEntrega);
                    if (res.data[0].cotizacion) {
                        res.data[0].monto = `$ ${formatMoney(res.data[0].cotizacion.total)}`
                    } else {
                        res.data[0].monto = `$ ${formatMoney(0)}`
                    }
                    setData(res.data);
                }
                else {
                    const tmp = res.data.map((item) => {
                        item.fechaDeCotizacion = DateTime.fromISO(item.fechaDeCotizacion).toJSDate();
                        item.fechaDeEntrega = getDate(item.fechaDeEntrega);
                        if (item.cotizacion) {
                            return { ...item, monto: `$ ${formatMoney(item.cotizacion.total)}` }
                        } else {
                            return { ...item, monto: `$ ${formatMoney(0)}` }
                        }
                    });
                    setData(tmp);
                }
                document.title = 'Cotizaciones';
            }
        } catch (err) {
            // setApiRes(state => { return { ...state, show: true, message: 'Ocurrió un error al cargar las cotizaciones, favor de intentarlo mas tarde', severity: 'error' } });
            console.log(err);
        }
    }

    useEffect(() => {
        getData();
    }, [])

    const renderProducts = () => {
        return (
            <div >
                <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'start', alignItems: 'center' }}>
                    <Button size='small' variant='outlined' onClick={() => navigate('/dashboard')}><i className='fad fa-arrow-left'></i> &nbsp;&nbsp;Regresar</Button>
                    <Typography variant='h5' align='left' sx={{ mt: 1, mb: 1, fontWeight: 700, ml: 3 }}>Cotizaciones</Typography>
                </div>
                <DataGrid
                    autoPageSize={true}
                    sx={{ '& .MuiDataGrid-toolbarContainer': { position: 'fixed', top: '70px', right: '24px' }, '& .MuiButtonBase-root': { color: 'comp.gray' }, '& .MuiDataGrid-row': { backgroundColor: 'comp.tableBg', color: 'text.primary', height: 'auto' }, '& .MuiDataGrid-columnHeader': { backgroundColor: 'comp.tableHeaderBg', color: 'text.primary' } }}
                    style={{ height: `${height - 120}px`, width: '100%', overflow: 'hidden' }}
                    localeText={LocaleTextDataGrid}
                    rows={data}
                    density='compact'
                    getRowId={(row) => row._id}
                    onCellClick={(e) => { setSelected(e.row._id) }}
                    onCellDoubleClick={(e) => { navigate(`/cotizaciones/cotizacion/${e.row._id}`, { target: '_blank' }) }}
                    columns={[
                        { field: 'noOrden', headerName: '#', flex: 1, minWidth: 60, maxWidth: 70, headerAlign: 'center', align: 'center' },
                        { field: 'cliente', headerName: 'Cliente', align: 'center', headerAlign: 'center', flex: 1, minWidth: 200, maxWidth: 350 },
                        {
                            field: 'estatus',
                            flex: 1,
                            maxWidth: 160,
                            headerName: 'Estatus',
                            headerClassName: 'text-bold',
                            align: 'center',
                            headerAlign: 'center',
                            renderCell: (params) => (
                                <CustomStatusChip estatus={params.row.estatus} label={params.row.estatus} />
                            ),
                        },
                        { field: 'fechaDeCotizacion', flex: 1, type: 'date', headerName: 'Fecha de Cotización', align: 'center', headerAlign: 'center', maxWidth: 150 },
                        { field: 'monto', flex: 1, headerName: 'Monto', align: 'center', headerAlign: 'center', maxWidth: 150, cellClassName: 'text-bold' },
                        { field: 'operacion', flex: 1, headerName: 'Operación', align: 'center', headerAlign: 'center', maxWidth: 200, },
                        { field: 'descripcion', flex: 1, headerName: 'Descripción', align: 'center', headerAlign: 'center', minWidth: 200, },
                        { field: 'vendedor', flex: 1, headerName: 'Vendedor', align: 'center', headerAlign: 'center', maxWidth: 200, },
                        { field: 'fechaDeEntrega', flex: 1, headerName: 'Fecha de Entrega', align: 'center', headerAlign: 'center', maxWidth: 150 },
                        { field: 'observaciones', flex: 1, headerName: 'Observaciones', align: 'center', headerAlign: 'center', minWidth: 160 }

                    ]}
                    pagination
                    slots={{
                        pagination: CustomPagination,
                        toolbar: GridToolbar
                    }}
                    initialState={{
                        ...data.initialState,
                        pagination: { paginationModel: { pageSize: 25 } },
                        columns: {
                            columnVisibilityModel: {
                                operacion: false,
                            },
                        }
                    }}
                />
            </div>
        )
    }

    const handleOpen = () => {
        setOpen(true);
    }

    const handleClose = () => {
        setOpen(false);
        setKey('');
        setSelected('');
    }

    const renderDelete = () => {
        return (
            <Dialog
                open={open}
                onClose={handleClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">Eliminar cotización</DialogTitle>
                <DialogContent>
                    <Typography variant='body1' align='justify'>
                        ¿Estás seguro que deseas eliminar la cotización seleccionada?
                    </Typography>
                    <Typography variant='body2' align='justify' sx={{ mt: 2 }}>
                        Esta acción no se puede deshacer.
                    </Typography>
                    <FormControl sx={{ mt: 3, minWidth: 100 }} style={{ display: 'flex' }}>
                        <TextField label="Pin" variant="outlined" value={key} onChange={(e) => setKey(e.target.value)} helperText="Ingrese el pin para confirmar" />
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} color="primary">
                        Cancelar
                    </Button>
                    <Button onClick={handleDelete} color="primary" autoFocus>
                        Eliminar
                    </Button>
                </DialogActions>
            </Dialog>
        )
    }

    const handleDelete = async () => {
        try {
            const res = await axios.post(`/api/orders/delete`, { order: { _id: selected }, key });
            if (res.status === 200) {
                setApiRes(state => { return { ...state, show: true, message: 'Cotización eliminada correctamente', severity: 'success' } });
                setSelected('');
                setKey('');
                handleClose();
                getData();
            }
        } catch (err) {
            setApiRes(state => { return { ...state, show: true, message: 'Pin incorrecto', severity: 'error' } });
        }
    }

    return (
        <Container maxWidth='xxl' sx={{ width: '100%', overflow: 'hidden', margin: '0px', padding: '4px 4px !important' }}>
            <Paper>
                <Link to='/cotizaciones/nueva'>
                    <Tooltip title='Nueva cotización' placement='top' arrow>
                        <Fab aria-label="add" color='success' style={{ position: 'fixed', bottom: '8px', left: '8px', zIndex: '10', color: '#FFF' }}>
                            <i className="fad fa-plus fa-2x"></i>
                        </Fab>
                    </Tooltip>
                </Link>
                <Link target='_blank' to={{ pathname: '/cotizaciones/seguimiento/' }} style={{ textDecoration: 'none' }}>
                    <Tooltip title='Seguimiento' placement='top' arrow>
                        <Fab aria-label="add" color='primary' style={{ position: 'fixed', bottom: '8px', left: '232px', zIndex: '10', color: '#FFF' }}>
                            <i className="fad fa-tasks-alt fa-2x"></i>
                        </Fab>
                    </Tooltip>
                </Link>

                {selected !== '' ?
                    <div>
                        <Link target='_blank' to={{ pathname: '/cotizaciones/editar/' + selected, state: { id: selected } }} style={{ textDecoration: 'none' }}>
                            <Tooltip title='Editar Información' placement='top' arrow>
                                <Fab aria-label="add" color='info' style={{ position: 'fixed', bottom: '8px', left: '98px', zIndex: '10', color: '#FFF' }}>
                                    <i className="fad fa-info-circle fa-2x"></i>
                                </Fab>
                            </Tooltip>
                        </Link>

                        <Link target='_blank' to={{ pathname: '/cotizaciones/cotizacion/' + selected, state: { id: selected } }} style={{ textDecoration: 'none' }}>
                            <Tooltip title='Editar cotización' placement='top' arrow>
                                <Fab aria-label="add" color='warning' style={{ position: 'fixed', bottom: '8px', left: '165px', zIndex: '10', color: '#FFF' }}>
                                    <i className="fad fa-edit fa-2x"></i>
                                </Fab>
                            </Tooltip>
                        </Link>

                        <Tooltip title='Eliminar cotización' placement='top' arrow>
                            <Fab aria-label="add" color='error' style={{ position: 'fixed', bottom: '8px', left: '300px', zIndex: '10', color: '#FFF' }} onClick={handleOpen}>
                                <i className="fad fa-trash fa-2x"></i>
                            </Fab>
                        </Tooltip>

                    </div>
                    : null}
                {renderProducts()}
                {renderDelete()}
                <CustomMessage open={apiRes.show} message={apiRes.message} severity={apiRes.severity} handleCloseMessage={handleCloseMessage} />
            </Paper>
        </Container>
    );
}

function Pagination({ page, onPageChange, className }) {
    const apiRef = useGridApiContext();
    const pageCount = useGridSelector(apiRef, gridPageCountSelector);

    return (
        <MuiPagination
            color="primary"
            className={className}
            count={pageCount}
            page={page + 1}
            onChange={(event, newPage) => {
                onPageChange(event, newPage - 1);
            }}
        />
    );
}

function CustomPagination(props) {
    return <GridPagination ActionsComponent={Pagination} {...props} />;
}

export default TablaCotizaciones;